import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getPasscodesForRange, getCurrentPasscode } from '../utils/passcodeGenerator';
import { format, addDays, subDays } from 'date-fns';

const { FiArrowLeft, FiLogOut, FiCalendar, FiCopy, FiCheck, FiChevronLeft, FiChevronRight, FiSettings, FiKey, FiLock, FiEye, FiEyeOff, FiX, FiClock, FiInfo } = FiIcons;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(new Date());
  const [passcodes, setPasscodes] = useState([]);
  const [currentPasscode, setCurrentPasscode] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetTimeModal, setShowResetTimeModal] = useState(false);
  const [resetHour, setResetHour] = useState(7);
  const [resetMinute, setResetMinute] = useState(0);

  useEffect(() => {
    // Check authentication
    const isAuthenticated = localStorage.getItem('adminAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }

    // Get stored reset time or use default (7:00)
    const storedResetHour = parseInt(localStorage.getItem('passcodeResetHour') || '7');
    const storedResetMinute = parseInt(localStorage.getItem('passcodeResetMinute') || '0');
    setResetHour(storedResetHour);
    setResetMinute(storedResetMinute);
    
    // Load passcodes for the selected date range
    setPasscodes(getPasscodesForRange(startDate, 7, storedResetHour, storedResetMinute));
    setCurrentPasscode(getCurrentPasscode(storedResetHour, storedResetMinute));
  }, [navigate, startDate]);

  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    navigate('/admin');
  };

  const handlePreviousWeek = () => {
    setStartDate(prevDate => subDays(prevDate, 7));
  };

  const handleNextWeek = () => {
    setStartDate(prevDate => addDays(prevDate, 7));
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const openPasswordModal = () => {
    setShowPasswordModal(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
  };

  const openResetTimeModal = () => {
    setShowResetTimeModal(true);
  };

  const closeResetTimeModal = () => {
    setShowResetTimeModal(false);
  };

  const handleChangePassword = () => {
    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Vui lòng điền đầy đủ thông tin');
      return;
    }

    // Check if current password is correct
    const storedPassword = localStorage.getItem('adminPassword') || 'admin123';
    if (currentPassword !== storedPassword) {
      setPasswordError('Mật khẩu hiện tại không chính xác');
      return;
    }

    // Check if new passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('Mật khẩu mới không khớp');
      return;
    }

    // Check if new password is strong enough
    if (newPassword.length < 6) {
      setPasswordError('Mật khẩu mới phải có ít nhất 6 ký tự');
      return;
    }

    // Save new password
    localStorage.setItem('adminPassword', newPassword);
    setPasswordSuccess('Mật khẩu đã được cập nhật thành công!');

    // Reset fields
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');

    // Close modal after success
    setTimeout(() => {
      setPasswordSuccess('');
      setShowPasswordModal(false);
    }, 2000);
  };

  const handleSaveResetTime = () => {
    // Save reset time to localStorage
    localStorage.setItem('passcodeResetHour', resetHour.toString());
    localStorage.setItem('passcodeResetMinute', resetMinute.toString());

    // Update passcodes with new reset time
    setPasscodes(getPasscodesForRange(startDate, 7, resetHour, resetMinute));
    setCurrentPasscode(getCurrentPasscode(resetHour, resetMinute));

    // Close modal
    setShowResetTimeModal(false);
  };

  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  // Generate hours array for dropdown (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  // Generate minutes array for dropdown (0, 15, 30, 45)
  const minutes = [0, 15, 30, 45];

  return (
    <div className="min-h-screen p-4 flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex gap-2">
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={openResetTimeModal}
            className="bg-purple-500/20 hover:bg-purple-500/30 text-white p-3 rounded-full transition-all"
            title="Thiết lập thời gian reset mã truy cập"
          >
            <SafeIcon icon={FiClock} className="text-xl" />
          </motion.button>
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={openPasswordModal}
            className="bg-blue-500/20 hover:bg-blue-500/30 text-white p-3 rounded-full transition-all"
            title="Đổi mật khẩu"
          >
            <SafeIcon icon={FiKey} className="text-xl" />
          </motion.button>
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={handleLogout}
            className="bg-red-500/20 hover:bg-red-500/30 text-white p-3 rounded-full transition-all"
          >
            <SafeIcon icon={FiLogOut} className="text-xl" />
          </motion.button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="flex justify-center mb-4">
          <img
            src="https://app1.sharemyimage.com/2025/06/10/logo_1736360407_LUX-HOME-22-1024x368.png"
            alt="Lux Homestay"
            className="h-20 object-contain filter drop-shadow-lg brightness-125 contrast-110"
          />
        </div>
        <h1 className="text-4xl font-bold text-white mb-2">Passcode Admin</h1>
        <p className="text-pink-200">Manage and view daily passcodes</p>
        <div className="mt-2 bg-white/10 px-3 py-1 rounded-full inline-block">
          <SafeIcon icon={FiClock} className="inline mr-1 text-pink-300" />
          <span className="text-pink-300 text-sm">
            Passcodes reset at {resetHour}:{resetMinute.toString().padStart(2, '0')} AM daily
          </span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-lg p-6 rounded-3xl mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-white">Current Passcode</h2>
          <div className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm">Active Now</div>
        </div>
        <div className="bg-white/10 p-4 rounded-xl flex items-center justify-between">
          <div>
            <p className="text-pink-200 text-sm">Today's passcode:</p>
            <p className="text-3xl font-bold text-white tracking-widest">{currentPasscode}</p>
            <p className="text-pink-200 text-xs mt-1">
              Valid until {resetHour}:{resetMinute.toString().padStart(2, '0')} AM tomorrow
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => copyToClipboard(currentPasscode, 'current')}
            className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all"
          >
            <SafeIcon
              icon={copiedIndex === 'current' ? FiCheck : FiCopy}
              className={`text-xl ${copiedIndex === 'current' ? 'text-green-400' : 'text-white'}`}
            />
          </motion.button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg p-6 rounded-3xl flex-1"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Passcode Schedule</h2>
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePreviousWeek}
              className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all"
            >
              <SafeIcon icon={FiChevronLeft} className="text-white" />
            </motion.button>
            <div className="bg-white/10 px-3 py-1 rounded-full text-sm text-white">
              <SafeIcon icon={FiCalendar} className="inline mr-1" />
              {format(startDate, 'MMM d')} - {format(addDays(startDate, 6), 'MMM d, yyyy')}
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNextWeek}
              className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all"
            >
              <SafeIcon icon={FiChevronRight} className="text-white" />
            </motion.button>
          </div>
        </div>

        <div className="space-y-3">
          {passcodes.map((item, index) => (
            <div
              key={item.date}
              className={`bg-white/5 hover:bg-white/10 p-4 rounded-xl flex items-center justify-between transition-all ${
                item.passcode === currentPasscode ? 'border-l-4 border-green-500' : ''
              }`}
            >
              <div>
                <p className="text-white font-medium">{item.displayDate}</p>
                <p className="text-pink-200 text-xs">
                  Passcode: <span className="text-white font-mono tracking-wider">{item.passcode}</span>
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => copyToClipboard(item.passcode, index)}
                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all"
              >
                <SafeIcon
                  icon={copiedIndex === index ? FiCheck : FiCopy}
                  className={`${copiedIndex === index ? 'text-green-400' : 'text-white'}`}
                />
              </motion.button>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="mt-6 text-center">
        <p className="text-pink-200 text-xs">
          © {new Date().getFullYear()} Lux Homestay. Passcodes automatically change at {resetHour}:{resetMinute.toString().padStart(2, '0')} AM daily.
        </p>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="bg-gradient-to-br from-purple-900 to-pink-900 p-8 rounded-3xl w-full max-w-md relative"
          >
            <button
              onClick={closePasswordModal}
              className="absolute top-4 right-4 text-white/60 hover:text-white"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
            <div className="text-center mb-6">
              <div className="bg-blue-500/30 p-3 rounded-full inline-block mb-2">
                <SafeIcon icon={FiLock} className="text-3xl text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Đổi Mật Khẩu Admin</h2>
              <p className="text-pink-200 text-sm mt-1">
                Mật khẩu mới sẽ được sử dụng cho các lần đăng nhập tiếp theo
              </p>
            </div>
            <div className="space-y-4">
              {/* Current Password Field */}
              <div>
                <label className="block text-pink-200 text-sm font-medium mb-2">
                  Mật khẩu hiện tại
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full p-3 pr-10 rounded-xl bg-white/10 text-white placeholder-pink-200/50 border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                    placeholder="Nhập mật khẩu hiện tại"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-pink-300 hover:text-pink-100"
                  >
                    <SafeIcon
                      icon={showCurrentPassword ? FiEyeOff : FiEye}
                      className="text-lg"
                    />
                  </button>
                </div>
              </div>

              {/* New Password Field */}
              <div>
                <label className="block text-pink-200 text-sm font-medium mb-2">
                  Mật khẩu mới
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full p-3 pr-10 rounded-xl bg-white/10 text-white placeholder-pink-200/50 border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                    placeholder="Nhập mật khẩu mới"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-pink-300 hover:text-pink-100"
                  >
                    <SafeIcon
                      icon={showNewPassword ? FiEyeOff : FiEye}
                      className="text-lg"
                    />
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-pink-200 text-sm font-medium mb-2">
                  Xác nhận mật khẩu mới
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-3 pr-10 rounded-xl bg-white/10 text-white placeholder-pink-200/50 border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                    placeholder="Nhập lại mật khẩu mới"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-pink-300 hover:text-pink-100"
                  >
                    <SafeIcon
                      icon={showConfirmPassword ? FiEyeOff : FiEye}
                      className="text-lg"
                    />
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {passwordError && (
                <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm">
                  {passwordError}
                </div>
              )}

              {/* Success Message */}
              {passwordSuccess && (
                <div className="bg-green-500/20 text-green-200 p-3 rounded-xl text-sm">
                  {passwordSuccess}
                </div>
              )}

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleChangePassword}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all duration-300 mt-2"
              >
                Cập Nhật Mật Khẩu
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Reset Time Modal */}
      {showResetTimeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="bg-gradient-to-br from-purple-900 to-pink-900 p-8 rounded-3xl w-full max-w-md relative"
          >
            <button
              onClick={closeResetTimeModal}
              className="absolute top-4 right-4 text-white/60 hover:text-white"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
            <div className="text-center mb-6">
              <div className="bg-purple-500/30 p-3 rounded-full inline-block mb-2">
                <SafeIcon icon={FiClock} className="text-3xl text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Thiết Lập Thời Gian Reset</h2>
              <p className="text-pink-200 text-sm mt-1">
                Thiết lập thời gian mã truy cập sẽ tự động thay đổi mỗi ngày
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-pink-200 text-sm font-medium mb-2">
                    Giờ
                  </label>
                  <select
                    value={resetHour}
                    onChange={(e) => setResetHour(parseInt(e.target.value))}
                    className="w-full p-3 rounded-xl bg-white/10 text-white border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                  >
                    {hours.map((hour) => (
                      <option key={hour} value={hour} className="bg-gray-800">
                        {hour}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex-1">
                  <label className="block text-pink-200 text-sm font-medium mb-2">
                    Phút
                  </label>
                  <select
                    value={resetMinute}
                    onChange={(e) => setResetMinute(parseInt(e.target.value))}
                    className="w-full p-3 rounded-xl bg-white/10 text-white border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                  >
                    {minutes.map((minute) => (
                      <option key={minute} value={minute} className="bg-gray-800">
                        {minute.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="bg-blue-500/20 p-4 rounded-xl text-sm text-blue-200">
                <p>
                  <SafeIcon icon={FiInfo} className="inline mr-1" />
                  Mã truy cập sẽ tự động thay đổi vào lúc {resetHour}:{resetMinute.toString().padStart(2, '0')} sáng mỗi ngày
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleSaveResetTime}
                className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all duration-300 mt-2"
              >
                Lưu Thiết Lập
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;